import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { getRemnaSub } from '../cabinet.api';
import { CabinetView } from '../cabinet.schemas';

type PlatformKey = 'apple' | 'android' | 'windows' | 'linux' | 'unknown';

type AppConfig = {
  name: string;
  link: string;
  deeplinkScheme?: string;
  appleIdAvailability?: 'russian' | 'not-russian';
};

type ImportPageProps = {
  onSelectView: (view: CabinetView) => void;
};

const apps: Record<Exclude<PlatformKey, 'unknown'>, AppConfig[]> = {
  apple: [
    {
      name: 'INCY',
      deeplinkScheme: 'incy',
      link: 'https://apps.apple.com/ru/app/incy/id6756943388',
      appleIdAvailability: 'russian',
    },
    {
      name: 'Happ',
      deeplinkScheme: 'happ',
      link: 'https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973',
      appleIdAvailability: 'not-russian',
    },
  ],
  android: [
    {
      name: 'Happ',
      deeplinkScheme: 'happ',
      link: 'https://play.google.com/store/apps/details?id=com.happproxy',
    },
  ],
  windows: [
    {
      name: 'Happ',
      link: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/setup-Happ.x64.exe',
    },
  ],
  linux: [
    {
      name: 'Hiddify',
      link: 'https://github.com/hiddify/hiddify-next/releases/latest/download/Hiddify-Linux-x64.AppImage',
    },
  ],
};

const platformLabels: Record<PlatformKey, string> = {
  apple: 'iOS / macOS',
  android: 'Android',
  windows: 'Windows',
  linux: 'Linux',
  unknown: 'Не определена',
};

function PlatformIcon({ platform }: { platform: Exclude<PlatformKey, 'unknown'> }) {
  const common = {
    className: 'platform-choice-svg',
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  switch (platform) {
    case 'apple':
      return (
        <svg {...common}>
          <path d="M15.5 4.1c-.9.5-1.6 1.5-1.5 2.6 1-.1 2-.7 2.6-1.5.6-.8.9-1.8.8-2.7-.7 0-1.4.4-1.9.8v.8Z" fill="currentColor" />
          <path d="M18.5 16.1c-.4.9-.6 1.3-1.1 2.1-.7 1.1-1.7 2.5-2.9 2.5-1.1 0-1.4-.7-2.8-.7s-1.8.7-2.9.7c-1.2 0-2.1-1.3-2.8-2.4-1.9-3-2.1-6.5-.9-8.4.8-1.4 2.2-2.2 3.5-2.2s2.2.7 3.3.7c1 0 1.7-.7 3.2-.7 1.1 0 2.3.6 3.1 1.7-2.7 1.5-2.3 5.4.3 6.7Z" fill="currentColor" />
        </svg>
      );
    case 'android':
      return (
        <svg {...common}>
          <path d="M7.2 9.5h9.6v7.2c0 1-.8 1.8-1.8 1.8H9c-1 0-1.8-.8-1.8-1.8V9.5Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="m8.4 5.4 1.2 2M15.6 5.4l-1.2 2M7 9.5c.5-2.2 2.4-3.7 5-3.7s4.5 1.5 5 3.7M5.2 10.4v4.8M18.8 10.4v4.8M9.8 18.4v2M14.2 18.4v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M10 9.2h.01M14 9.2h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case 'windows':
      return (
        <svg {...common}>
          <path d="M4 5.4 10.8 4.5v7H4v-6.1ZM12.3 4.3 20 3.2v8.3h-7.7V4.3ZM4 12.9h6.8v6.7L4 18.7v-5.8ZM12.3 12.9H20v7.9l-7.7-1v-6.9Z" fill="currentColor" />
        </svg>
      );
    case 'linux':
      return (
        <svg {...common}>
          <path d="M8 18.8h8M7.5 15.8c.2-5.1 1.4-10.4 4.5-10.4s4.3 5.3 4.5 10.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M9 11.2h6M9.5 15.8h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M10.2 8.3h.01M13.8 8.3h.01" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
      );
  }
}

function getTelegramPlatform() {
  const telegramWindow = window as Window & {
    Telegram?: {
      WebApp?: {
        platform?: string;
      };
    };
  };

  return telegramWindow.Telegram?.WebApp?.platform?.toLowerCase() || '';
}

function detectPlatform(): PlatformKey {
  const telegramPlatform = getTelegramPlatform();
  const userAgent = navigator.userAgent.toLowerCase();
  const platformSource = `${telegramPlatform} ${userAgent}`;

  if (
    platformSource.includes('ios')
    || platformSource.includes('macos')
    || platformSource.includes('iphone')
    || platformSource.includes('ipad')
    || platformSource.includes('macintosh')
    || platformSource.includes('mac os')
  ) {
    return 'apple';
  }

  if (platformSource.includes('android')) {
    return 'android';
  }

  if (platformSource.includes('windows')) {
    return 'windows';
  }

  if (platformSource.includes('linux')) {
    return 'linux';
  }

  return 'unknown';
}

function getSubFromUrl() {
  const searchSub = new URLSearchParams(window.location.search).get('sub');

  if (searchSub) {
    return searchSub;
  }

  const hashQuery = window.location.hash.split('?')[1];
  return hashQuery ? new URLSearchParams(hashQuery).get('sub') || '' : '';
}

function getConfigStatus(isLoading: boolean, hasError: boolean, hasNoSubscription: boolean, sub: string) {
  if (isLoading) {
    return 'Загружаем конфиг...';
  }

  if (hasNoSubscription) {
    return 'Нет активной подписки';
  }

  if (hasError) {
    return 'Не удалось получить конфиг';
  }

  if (sub) {
    return 'Конфиг готов к импорту';
  }

  return 'Конфиг не найден';
}

function getImportLink(appName: string, config: string) {
  const normalizedName = appName.toLowerCase();

  if (normalizedName === 'v2raytun') {
    return `${normalizedName}://import/${config}#⚡️MifVPN`;
  }

  if (normalizedName === 'happ') {
    return `${normalizedName}://add/${config}`;
  }

  return `${normalizedName}://import/${config}#⚡️MifVPN`;
}

function canUseAppDeeplink(app: AppConfig, platform: PlatformKey) {
  return Boolean(app.deeplinkScheme && (platform === 'apple' || platform === 'android'));
}

function AppleIdBadge({ availability }: { availability: AppConfig['appleIdAvailability'] }) {
  if (!availability) {
    return null;
  }

  const isNotRussian = availability === 'not-russian';

  return (
    <span className={`apple-id-badge ${isNotRussian ? 'blocked' : ''}`}>
      <span className="russia-flag" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span>{isNotRussian ? 'Не для российского Apple ID' : 'Российский Apple ID'}</span>
    </span>
  );
}

function isRemnawaveUserNotFound(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) {
    return false;
  }

  const data = error.response.data;
  return Boolean(
    data
    && typeof data === 'object'
    && 'detail' in data
    && data.detail === 'Remnawave user not found',
  );
}

export function ImportPage({ onSelectView }: ImportPageProps) {
  const detectedPlatform = useMemo(() => detectPlatform(), []);
  const [platform, setPlatform] = useState<PlatformKey>(detectedPlatform);
  const [sub, setSub] = useState(() => getSubFromUrl());
  const [isLoadingSub, setIsLoadingSub] = useState(true);
  const [hasSubError, setHasSubError] = useState(false);
  const [hasNoSubscription, setHasNoSubscription] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const selectedApps = platform === 'unknown' ? [] : apps[platform];
  const selectedApp = selectedApps[0];
  const canUseDeeplink = selectedApp ? canUseAppDeeplink(selectedApp, platform) : false;
  const configStatus = getConfigStatus(isLoadingSub, hasSubError, hasNoSubscription, sub);

  const platformOptions = useMemo(
    () => ([
      { id: 'apple' as const, label: 'iOS / macOS' },
      { id: 'android' as const, label: 'Android' },
      { id: 'windows' as const, label: 'Windows' },
      { id: 'linux' as const, label: 'Linux' },
    ]),
    [],
  );

  useEffect(() => {
    let isMounted = true;

    async function loadSub() {
      setIsLoadingSub(true);
      setHasSubError(false);
      setHasNoSubscription(false);

      try {
        const data = await getRemnaSub();

        if (isMounted && data.sub) {
          setSub(data.sub);
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return;
        }

        if (isRemnawaveUserNotFound(error)) {
          if (isMounted) {
            setHasNoSubscription(true);
            setSub('');
          }

          return;
        }

        if (isMounted) {
          setHasSubError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingSub(false);
        }
      }
    }

    loadSub();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDownloadClient = (app: AppConfig) => {
    window.open(app.link, '_blank', 'noopener,noreferrer');
  };

  const handleImportConfig = (app: AppConfig) => {
    if (!app.deeplinkScheme || !sub) {
      return;
    }

    window.location.href = getImportLink(app.name, encodeURIComponent(sub));
  };

  const handleCopyConfig = async () => {
    if (!sub) {
      setActionMessage('Конфиг пока не найден');
      return;
    }

    try {
      await navigator.clipboard.writeText(sub);
      setActionMessage('Конфиг скопирован');
    } catch {
      setActionMessage('Не удалось скопировать конфиг');
    }
  };

  return (
    <div className="cabinet-page">
      <h1>Подключение и импорт конфигурации</h1>
      <div className="import-grid">
        <section className="cabinet-panel platform-panel">
          <h2>Платформа</h2>
          {platformOptions.map((option) => (
            <button
              className={`${platform === option.id ? 'active' : ''}${detectedPlatform === option.id ? ' detected' : ''}`}
              type="button"
              onClick={() => setPlatform(option.id)}
              aria-label={`${option.label}${detectedPlatform === option.id ? ', определено автоматически' : ''}`}
              key={option.id}
            >
              <span className="platform-choice-main">
                <span className={`platform-choice-icon ${option.id}`}>
                  <PlatformIcon platform={option.id} />
                </span>
                <span className="platform-choice-label">{option.label}</span>
              </span>
              {detectedPlatform === option.id ? <strong className="platform-detected-label">Определено</strong> : null}
            </button>
          ))}
        </section>

        <section className="cabinet-panel connect-panel">
          <span className={`import-status ${hasSubError || hasNoSubscription ? 'error' : sub ? 'ready' : ''}`}>{configStatus}</span>
          <h2>{hasNoSubscription ? 'Подписка не активна' : platform === 'unknown' ? 'Ваша платформа не поддерживается' : 'Подключитесь за минуту'}</h2>
          <p>
            {hasNoSubscription
              ? 'Чтобы получить конфиг для подключения, оформите подписку в магазине.'
              : platform === 'unknown'
              ? 'Не удалось определить подходящий клиент. Напишите в поддержку, и мы поможем подключить устройство вручную.'
              : 'Скачайте клиент, затем добавьте конфиг из личного кабинета MifVPN.'}
          </p>

          {hasNoSubscription ? (
            <div className="import-subscription-empty">
              <strong>Конфиг появится после оплаты</strong>
              <span>После покупки тарифа вернитесь на эту страницу и импортируйте подключение.</span>
              <button className="button button-primary" type="button" onClick={() => onSelectView('shop')}>Оформить подписку</button>
            </div>
          ) : null}

          {selectedApps.length > 0 && !hasNoSubscription ? (
            <div className="import-app-list">
              {selectedApps.map((app, index) => {
                const appCanUseDeeplink = canUseAppDeeplink(app, platform);

                return (
                  <div className={`import-app-card ${index === 0 ? 'primary' : 'secondary'}`} key={app.name}>
                    <div className="import-app-info">
                      <span>{index === 0 ? 'Рекомендуемое приложение' : 'Альтернатива'}</span>
                      <strong>{app.name}</strong>
                      <small>{platformLabels[platform]}</small>
                      <AppleIdBadge availability={app.appleIdAvailability} />
                    </div>
                    <div className="import-card-actions">
                      <button className="button button-secondary" type="button" onClick={() => handleDownloadClient(app)}>
                        Скачать {app.name}
                      </button>
                      {appCanUseDeeplink ? (
                        <button className="button button-primary" disabled={!sub || isLoadingSub} type="button" onClick={() => handleImportConfig(app)}>
                          Добавить конфиг
                        </button>
                      ) : (
                        <button className="button button-primary" disabled={!sub || isLoadingSub} type="button" onClick={handleCopyConfig}>
                          Скопировать конфиг
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {actionMessage ? <div className="import-action-message">{actionMessage}</div> : null}
        </section>

        <section className="qr-panel">
          <h2>QR Scanner</h2>
          <p>Откройте клиент и сканируйте QR-код для быстрого импорта без ручной вставки строки конфигурации.</p>
        </section>

        <section className="cabinet-panel quick-panel">
          <h2>Быстрые действия</h2>
          <p>
            1. Скачайте {selectedApp?.name || 'подходящий клиент'}
            <br />
            2. {canUseDeeplink ? 'Нажмите Добавить конфиг' : 'Скопируйте конфиг'}
            <br />
            3. Проверьте подключение
          </p>
        </section>
      </div>
    </div>
  );
}
